import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography, useMediaQuery, Tooltip } from "@mui/material";
import TrendMappingSVG from "../../../../assets/svgs/TrendMapping.svg.js";
import styles from "../ChartDialogCommon.module.scss";
import ChartFilterButtons from "./ChartFilterButtons";
import ChartArrowButton from "./ChartArrowButton";
import LineChartDialog from "./LineChartDialog";
import ChartTotalContainer from "./ChartTotalContainer";
import { useSelector } from "react-redux";

export default function CardDetailChart({
	data,
	handleClickFilter,
	selectedFilter,
	queryFilters,
	queryFiltersComparison,
	filters,
	setQueryFilters,
	setQueryFiltersComparison,
	reloadChartData,
	total,
	censusAverage,
	handleChartLabelType,
	isChartTotalButton = false,
	totalText,
	censusSubtitle = "Of Avg. Census",
	isTotalText = true,
	isChartArrow = true,
	selectedFacility,
	isOverallTotal = false,
	dbData,
	isPlusSign = false,
	dailyCensusTotal = [],
	onOpenTrendMapping,
}) {
	const [chartData, setChartData] = useState([]);
	const [isSeparateLine, setIsSeparateLine] = useState(false);
	const [isShowPercentage, setIsShowPercentage] = useState(false);
	const { filterButtons, chartPointType } = queryFilters;
	const activeFacilities = useSelector((state) => state.activeFacilities.facilities);
	const isMobileWidth = useMediaQuery('(max-width: 768px)');
	const isMobileLandscape = useMediaQuery('(max-height: 500px) and (max-width: 960px)');
	const isMobileDevice = isMobileWidth || isMobileLandscape;
	const isTablet = useMediaQuery('(max-width: 960px)');

	useEffect(() => {
		setChartData(data);
	}, [data]);

	return (
		<Box container className={styles.chartBoxContainer} sx={{ pb: 2, ...(isMobileDevice && { height: "auto" }) }}>
			{!isMobileDevice && <div className={styles.title}>&nbsp;</div>}
			<Grid container className={styles.boxContainerInner} sx={isMobileDevice ? { display: "block" } : { height: "100%" }}>
				{isMobileDevice ? (
					<Grid item xs={12}>
						{/* Mobile Header Layout */}
						<Stack spacing={1} sx={{ mb: 1.5, width: "100%" }}>
							{/* Row 1: Filters (Left) & Totals (Right) combined into a single row */}
							<Box 
								sx={{ 
									display: "flex", 
									justifyContent: "space-between", 
									alignItems: "center", 
									flexWrap: "wrap",
									width: "100%", 
									px: 1,
									gap: 1.5
								}}
							>
								{/* Left: Plus/Minus Buttons & Filter Buttons */}
								<Box 
									sx={{ 
										display: "flex", 
										gap: 1.5, 
										alignItems: "center", 
										flexWrap: "wrap",
									}}
								>
									{isPlusSign && (
										<Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
											<Button
												variant={isShowPercentage ? "outlined" : "contained"}
												size="small"
												onClick={() => setIsShowPercentage(false)}
												sx={{
													fontSize: 10,
													height: 28,
													minWidth: 80,
													textTransform: "none",
													px: 1
												}}
											>
												Plus/Minus
											</Button>
											<Button
												variant={isShowPercentage ? "contained" : "outlined"}
												size="small"
												onClick={() => setIsShowPercentage(true)}
												sx={{
													fontSize: 10,
													height: 28,
													minWidth: 80,
													textTransform: "none",
													px: 1
												}}
											>
												Census
											</Button>
										</Stack>
									)}
									<Box sx={{ flexShrink: 0 }}>
										<ChartFilterButtons
											handleClickFilter={handleClickFilter}
											selectedFilter={selectedFilter}
											filterButtons={filterButtons}
											isMobile={isMobileDevice}
											isDisabled={!chartData || chartData.length === 0}
										/>
									</Box>
								</Box>

								{/* Right: Totals Container */}
								<Box sx={{ flexShrink: 0 }}>
									{isTotalText && (
										<ChartTotalContainer
											total={total}
											chartData={chartData}
											censusAverage={censusAverage}
											handleChartLabelType={handleChartLabelType}
											title={totalText}
											subtitle={censusSubtitle}
											isChartTotalButton={isChartTotalButton}
											chartPointType={chartPointType}
											isTotalText={isTotalText}
											dbData={dbData}
										/>
									)}
								</Box>
							</Box>

							{/* Row 2: Per Facility View Checkbox */}
							{activeFacilities && Array.isArray(activeFacilities) && activeFacilities.length > 1 && (
								<Box sx={{ ml: 1, mt: 0 }}>
									<FormControlLabel
										control={
											<Checkbox
												disableRipple
												checked={isSeparateLine}
												onChange={(e) => setIsSeparateLine(e.target.checked)}
												sx={{
													p: 0.5,
													'& .MuiSvgIcon-root': {
														fontSize: 16
													}
												}}
											/>
										}
										label={
											<Typography
												variant="subtitle2"
												component={"span"}
												sx={{ 
													fontFamily: "mont reg",
													fontSize: 12,
													lineHeight: 1.2
												}}
											>
												Per Facility View
											</Typography>
										}
									/>
								</Box>
							)}
						</Stack>

						{/* Legend */}
						{(() => {
							const hasProjectedPoints = Array.isArray(chartData) && chartData.some(item => 
								item?.isProjected === true || 
								(item?.data && Array.isArray(item.data) && item.data.some(point => point?.isProjected === true))
							);
							if (!hasProjectedPoints) return null;
							
							return (
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="flex-end"
									spacing={0.5}
									sx={{
										mb: 1,
										pr: 2,
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											width: 10,
											height: 10,
										}}
									>
										<svg width={10} height={10} viewBox="0 0 14 14">
											<rect
												x={2}
												y={2}
												width={10}
												height={10}
												fill="#FF8C00"
												stroke="#CC5500"
												strokeWidth={1.5}
												transform="rotate(45 7 7)"
											/>
										</svg>
									</Box>
									<Typography
										variant="caption"
										sx={{
											fontSize: 10,
											color: 'text.secondary',
											fontFamily: 'mont reg',
										}}
									>
										Projected Data (Incomplete Period)
									</Typography>
								</Stack>
							);
						})()}

						{/* Chart Area with Overlay Control Buttons */}
						<Box sx={{ position: "relative", width: "100%" }}>
							{/* Left Overlay Arrow Button */}
							{isChartArrow && (
								<Box
									sx={{
										position: "absolute",
										left: 4,
										top: "40%",
										transform: "translateY(-50%)",
										zIndex: 2,
										pointerEvents: "auto"
									}}
								>
									<ChartArrowButton
										queryFilters={queryFilters}
										setQueryFilters={setQueryFilters}
										queryFiltersComparison={queryFiltersComparison}
										setQueryFiltersComparison={setQueryFiltersComparison}
										selectedFilter={selectedFilter}
										type="left"
										filters={filters}
										reloadChartData={reloadChartData}
										isMobile={isMobileDevice}
									/>
								</Box>
							)}

							{/* Right Overlay Arrow Button */}
							{isChartArrow && (
								<Box
									sx={{
										position: "absolute",
										right: 4,
										top: "40%",
										transform: "translateY(-50%)",
										zIndex: 2,
										pointerEvents: "auto"
									}}
								>
									<ChartArrowButton
										queryFilters={queryFilters}
										selectedFilter={selectedFilter}
										type="right"
										filters={filters}
										setQueryFilters={setQueryFilters}
										reloadChartData={reloadChartData}
										isMobile={isMobileDevice}
									/>
								</Box>
							)}

							{/* Line Chart */}
							<Box sx={{ width: "100%", px: 0.5 }}>
								<LineChartDialog
									selectedFacility={selectedFacility}
									chartData={chartData}
									chartPointType={chartPointType}
									selectedFilter={selectedFilter}
									isSeparateLine={isSeparateLine}
									censusAverage={censusAverage}
									isOverallTotal={isOverallTotal}
									isPlusSign={isPlusSign}
									cardType={filters?.type}
									isShowCensus={isShowPercentage}
									dailyCensusTotal={dailyCensusTotal}
								/>
							</Box>
						</Box>
					</Grid>
								) : (
					// DESKTOP LAYOUT (Original Grid structure preserved with polished aesthetics)
					<>
						<Grid item xs={1} alignItems="center" container>
							{isChartArrow && (
								<ChartArrowButton
									queryFilters={queryFilters}
									setQueryFilters={setQueryFilters}
									queryFiltersComparison={queryFiltersComparison}
									setQueryFiltersComparison={setQueryFiltersComparison}
									selectedFilter={selectedFilter}
									type="left"
									filters={filters}
									reloadChartData={reloadChartData}
									isMobile={isMobileDevice}
								/>
							)}
						</Grid>
						<Grid
							item
							xs={10}
							sx={{
								position: "relative"
							}}
						>
							<>
							{onOpenTrendMapping && (
								<Stack 
									position={"absolute"} 
									sx={{ 
										left: isTablet ? "-5%" : "-8%", 
										top: isTablet ? "-2%" : "-1%",
										zIndex: 1
									}}
								>
									<Tooltip title={"Trend Mapping"} arrow>
										<Button
											color="inherit"
											variant="outlined"
											onClick={onOpenTrendMapping}
											sx={{
												borderRadius: "50%",
												minWidth: isTablet ? "40px" : "54px",
												height: isTablet ? "40px" : "54px",
												padding: 0,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												backgroundColor: 'rgba(72, 121, 245, 0.04)',
												borderColor: 'rgba(72, 121, 245, 0.15)',
												boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
												'&:hover': {
													backgroundColor: 'rgba(72, 121, 245, 0.08)',
													borderColor: 'primary.main',
													'& svg path:not([fill="transparent"])': {
														fill: 'primary.main',
													},
												}
											}}
										>
											<TrendMappingSVG
												style={{
													width: isTablet ? '26px' : '32px',
													height: isTablet ? '26px' : '32px',
													display: 'block',
													flexShrink: 0
												}}
											/>
										</Button>
									</Tooltip>
								</Stack>
							)}
								{isTotalText && (
									<Stack 
										justifyContent={"flex-end"} 
										position={"absolute"} 
										sx={{ 
											right: -30, 
											top: -25
										}}
									>
										<ChartTotalContainer
											total={total}
											chartData={chartData}
											censusAverage={censusAverage}
											handleChartLabelType={handleChartLabelType}
											title={totalText}
											subtitle={censusSubtitle}
											isChartTotalButton={isChartTotalButton}
											chartPointType={chartPointType}
											isTotalText={isTotalText}
											dbData={dbData}
										/>
									</Stack>
								)}
								{activeFacilities &&  Array.isArray(activeFacilities) &&  activeFacilities.length > 1 && (
									<Stack 
										sx={{ 
											mb: -2, 
											ml: 2.5, 
											width: "220px" 
										}} 
										direction={"row"}
									>
										<FormControlLabel
											control={
												<Checkbox
													disableRipple
													checked={isSeparateLine}
													onChange={(e) => setIsSeparateLine(e.target.checked)}
												/>
											}
											label={
												<Typography
													variant="subtitle2"
													component={"span"}
													sx={{ fontFamily: "mont reg" }}
													fontSize={14}
												>
													Per Facility View
												</Typography>
											}
										/>
									</Stack>
								)}
								<Stack 
									sx={{ 
										mt: isTotalText ? "25px" : "18px", 
										width: "100%" 
									}} 
									direction={"row"}
								>
									{isPlusSign && (
										<Stack direction="row" spacing={1} sx={{ mr: 2 }}>
											<Button
												variant={isShowPercentage ? "outlined" : "contained"}
												size="medium"
												onClick={() => setIsShowPercentage(false)}
												sx={{
													fontSize: 13,
													height: 34,
													minWidth: 120,
													textTransform: "none"
												}}
											>
												Plus/Minus
											</Button>
											<Button
												variant={isShowPercentage ? "contained" : "outlined"}
												size="medium"
												onClick={() => setIsShowPercentage(true)}
												sx={{
													fontSize: 13,
													height: 34,
													minWidth: 120,
													textTransform: "none"
												}}
											>
												Census
											</Button>
										</Stack>
									)}
									<ChartFilterButtons
										handleClickFilter={handleClickFilter}
										selectedFilter={selectedFilter}
										filterButtons={filterButtons}
										isMobile={isMobileDevice}
										isDisabled={!chartData || chartData.length === 0}
									/>
								</Stack>
								{/* Projection Legend - Show only if there are projected points */}
								{(() => {
									const hasProjectedPoints = Array.isArray(chartData) && chartData.some(item => 
										item?.isProjected === true || 
										(item?.data && Array.isArray(item.data) && item.data.some(point => point?.isProjected === true))
									);
									if (!hasProjectedPoints) return null;
									
									return (
										<Stack
											direction="row"
											alignItems="center"
											justifyContent="flex-end"
											spacing={1}
											sx={{
												mb: 1,
												mt: -0.5,
												pr: 2,
											}}
										>
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													width: 14,
													height: 14,
												}}
											>
												<svg
													width={14}
													height={14}
													viewBox="0 0 14 14"
												>
													<rect
														x={2}
														y={2}
														width={10}
														height={10}
														fill="#FF8C00"
														stroke="#CC5500"
														strokeWidth={1.5}
														transform="rotate(45 7 7)"
													/>
												</svg>
											</Box>
											<Typography
												variant="caption"
												sx={{
													fontSize: 12,
													color: 'text.secondary',
													fontFamily: 'mont reg',
												}}
											>
												Projected Data (Incomplete Period)
											</Typography>
										</Stack>
									);
								})()}
								<LineChartDialog
									selectedFacility={selectedFacility}
									chartData={chartData}
									chartPointType={chartPointType}
									selectedFilter={selectedFilter}
									isSeparateLine={isSeparateLine}
									censusAverage={censusAverage}
									isOverallTotal={isOverallTotal}
									isPlusSign={isPlusSign}
									cardType={filters?.type}
									isShowCensus={isShowPercentage}
									dailyCensusTotal={dailyCensusTotal}
								/>
							</>
						</Grid>
						<Grid item xs={1} alignItems="center" container className={styles.rightButtonContainer}>
							{isChartArrow && (
								<ChartArrowButton
									queryFilters={queryFilters}
									selectedFilter={selectedFilter}
									type="right"
									filters={filters}
									setQueryFilters={setQueryFilters}
									reloadChartData={reloadChartData}
									isMobile={isMobileDevice}
								/>
							)}
						</Grid>
					</>
				)}
			</Grid>
		</Box>
	);
}
